import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import {
  GetTenantDocument,
  InstallTenantProductDocument,
  type InstallTenantProductMutation,
  type InstallTenantProductMutationVariables,
} from "~/features/admin/tenants/gqls";
import { translateError } from "~/lib/errors";

interface UseTenantInstallProductOptions {
  onSuccess?: () => void;
}

/** Install a product to the tenant (the connection starts as private). */
export function useTenantInstallProduct({ onSuccess }: UseTenantInstallProductOptions = {}) {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [installProduct] = useMutation<InstallTenantProductMutation, InstallTenantProductMutationVariables>(
    InstallTenantProductDocument,
    { refetchQueries: [GetTenantDocument], awaitRefetchQueries: true },
  );

  const submit = useCallback(
    async (tenantId: string, productId: string) => {
      setProcessing(true);
      try {
        const res = await installProduct({ variables: { input: { id: tenantId, productId } } });
        match(res.data?.cnutInstallTenantProduct)
          .with({ __typename: "ValidationError" }, (e) => toast.error(e.fields.map((f) => f.message).join(", ")))
          .with({ __typename: "TenantInstallProductOutput" }, () => {
            toast.success(t("model.tenant.productInstalledSuccessfully"));
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [installProduct, t, onSuccess],
  );

  return { submit, processing };
}
